import { useEffect, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { LoadingGrid, StatCard } from "@/components/bits";
import { inr } from "@/lib/api";
import api from "@/lib/api";

const COLORS = ["#2563EB", "#F5A623", "#0B1B3D", "#10B981", "#8B5CF6", "#EC4899"];

export default function EmployerAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/employer/analytics").then(({ data }) => setData(data)).catch(() => setError(true));
  }, []);

  if (error)
    return (
      <div className="border border-dashed border-border rounded-xl py-16 text-center" data-testid="analytics-error">
        <p className="font-heading font-semibold text-lg text-[#0B1B3D]">Analytics unavailable</p>
        <p className="text-sm text-muted-foreground mt-2">Could not load analytics right now. Please retry in a moment.</p>
      </div>
    );
  if (!data) return <LoadingGrid count={4} />;

  return (
    <div data-testid="employer-analytics">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard label="Avg Time to Hire" value={`${data.time_to_hire_days} days`} testId="stat-tth" />
        <StatCard label="Join Conversion" value={`${data.conversion_rate}%`} accent testId="stat-conversion" />
        <StatCard label="Total Applications" value={data.totals?.applications ?? 0} />
        <StatCard label="Active Employees" value={data.totals?.employees ?? 0} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="funnel-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Hiring funnel</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.funnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="source-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Source performance</h3>
          {data.sources.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No sourcing data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.sources} dataKey="applications" nameKey="source" innerRadius={55} outerRadius={90} label>
                  {data.sources.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex gap-4 justify-center mt-2">
            {data.sources.map((s) => (
              <p key={s.source} className="text-xs text-muted-foreground capitalize">
                {s.source}: {s.joined}/{s.applications} joined
              </p>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="perjob-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Applications per job</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.per_job}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="title" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} tickFormatter={(t) => (t.length > 12 ? t.slice(0, 12) + "…" : t)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="applications" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="joined" fill="#F5A623" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="payroll-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Payroll trend (net pay)</h3>
          {data.payroll_trend.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">Run payroll to see trends.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.payroll_trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="net" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
